import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  useCreateDesignerExemplarMutation,
  useDeleteDesignerExemplarMutation,
  useGetDesignerCanvasClassesQuery,
  useGetDesignerExemplarCoverageQuery,
  useGetDesignerExemplarsQuery,
  useUpdateDesignerExemplarMutation,
} from 'src/redux/operations/operations.endpoints';
import type { DesignerExemplar, DesignerExemplarWritePayload } from '../types/designer-exemplar.types';
import {
  buildDesignerExemplarPayload,
  normaliseDesignerCanvasClasses,
  normaliseDesignerExemplars,
  parseDesignerExemplarMutationError,
} from '../helpers/designer-exemplar.helpers';
import { DesignerCanvasClassTable } from '../components/designer-canvas-class-table';
import { DesignerExemplarCoveragePanel } from '../components/designer-exemplar-coverage-panel';
import {
  DesignerExemplarList,
  type DesignerExemplarListFilters,
} from '../components/designer-exemplar-list';
import { DesignerExemplarSheet } from '../components/designer-exemplar-sheet';
import { SchedulerDsButton } from '../components/scheduler-ds-button';
import type { EntitySheetMode } from '../components/vertical-profile-sheet';

type ExemplarSheetState =
  | { open: false }
  | { open: true; mode: EntitySheetMode; item: DesignerExemplar | null };

const DEFAULT_FILTERS: DesignerExemplarListFilters = {
  canvasClass: 'all',
  vertical: 'all',
  extractionStatus: 'all',
  active: 'active',
};

export function DesignerExemplarsTab() {
  const [sheet, setSheet] = useState<ExemplarSheetState>({ open: false });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DesignerExemplarListFilters>(DEFAULT_FILTERS);

  const {
    data: exemplars,
    isLoading: exemplarsLoading,
    isError: exemplarsError,
    refetch: refetchExemplars,
    isFetching: exemplarsFetching,
  } = useGetDesignerExemplarsQuery();
  const {
    data: coverage,
    isLoading: coverageLoading,
    isError: coverageError,
    refetch: refetchCoverage,
  } = useGetDesignerExemplarCoverageQuery();
  const {
    data: canvasClasses,
    isLoading: canvasClassesLoading,
    isError: canvasClassesError,
    refetch: refetchCanvasClasses,
  } = useGetDesignerCanvasClassesQuery();

  const [createExemplar, { isLoading: isCreating }] = useCreateDesignerExemplarMutation();
  const [updateExemplar, { isLoading: isUpdating }] = useUpdateDesignerExemplarMutation();
  const [deleteExemplar, { isLoading: isDeleting }] = useDeleteDesignerExemplarMutation();

  const exemplarList = normaliseDesignerExemplars(exemplars);
  const canvasClassList = normaliseDesignerCanvasClasses(canvasClasses);
  const canEdit = !exemplarsError;
  const verticalOptions = useMemo(() => {
    const values = new Set(exemplarList.map((entry) => entry.vertical).filter(Boolean));
    return [...values].sort();
  }, [exemplarList]);

  const handleSubmit = async(values: DesignerExemplarWritePayload) => {
    try {
      if (sheet.open && sheet.mode === 'create') {
        const created = await createExemplar(buildDesignerExemplarPayload(values)).unwrap();
        setSubmitError(null);
        return created;
      }
      if (sheet.open && sheet.mode === 'edit' && sheet.item) {
        const updated = await updateExemplar({
          exemplarId: sheet.item.id,
          body: buildDesignerExemplarPayload(values),
        }).unwrap();
        setSubmitError(null);
        setSheet({ open: true, mode: 'edit', item: updated });
      }
    } catch (error) {
      setSubmitError(parseDesignerExemplarMutationError(error));
    }
  };

  const handleRetire = async() => {
    if (!sheet.open || sheet.mode !== 'edit' || !sheet.item) {
      return;
    }
    try {
      await deleteExemplar({ exemplarId: sheet.item.id }).unwrap();
      setSubmitError(null);
      setSheet({ open: false });
      setFilters((current) => ({ ...current, active: 'retired' }));
    } catch (error) {
      setSubmitError(parseDesignerExemplarMutationError(error));
    }
  };

  return (
    <div className="space-y-6">
      <DesignerExemplarCoveragePanel
        coverage={coverage}
        isLoading={coverageLoading}
        isError={coverageError}
      />

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {canEdit ? (
            <SchedulerDsButton
              type="button"
              onClick={() => {
                setSubmitError(null);
                setSheet({ open: true, mode: 'create', item: null });
              }}
            >
              <Plus className="size-4" />
              New exemplar
            </SchedulerDsButton>
          ) : null}
          <SchedulerDsButton
            type="button"
            variant="secondary"
            disabled={exemplarsFetching}
            onClick={() => {
              void refetchExemplars();
              void refetchCoverage();
              void refetchCanvasClasses();
            }}
          >
            Refresh exemplars
          </SchedulerDsButton>
        </div>

        <DesignerExemplarList
          exemplars={exemplarList}
          canvasClasses={canvasClassList}
          filters={filters}
          isLoading={exemplarsLoading}
          isError={exemplarsError}
          verticalOptions={verticalOptions}
          onFiltersChange={setFilters}
          onSelect={(exemplar) => {
            if (canEdit) {
              setSubmitError(null);
              setSheet({ open: true, mode: 'edit', item: exemplar });
            }
          }}
        />
      </div>

      <DesignerCanvasClassTable
        canvasClasses={canvasClassList}
        isLoading={canvasClassesLoading}
        isError={canvasClassesError}
      />

      <DesignerExemplarSheet
        open={sheet.open}
        mode={sheet.open ? sheet.mode : 'edit'}
        exemplar={sheet.open ? sheet.item : null}
        canvasClasses={canvasClassList}
        isSaving={isCreating || isUpdating}
        isRetiring={isDeleting}
        submitError={submitError}
        onOpenChange={(open) => {
          if (!open) {
            setSheet({ open: false });
          }
        }}
        onSubmit={handleSubmit}
        onRetire={handleRetire}
        onExemplarUpdated={(exemplar) => {
          if (sheet.open) {
            setSheet({ open: true, mode: 'edit', item: exemplar });
          }
        }}
      />
    </div>
  );
}
